import { Link, useNavigate } from 'react-router-dom';
import DataTable from '../../../components/table/DataTable';
import http from '../../../api/axios';
import { useState, useEffect } from 'react';
import { apiUrl } from '../../../api/apiUrl';
import { toast } from 'react-toastify';
import { useCurrentCompany } from '../../../hooks/useCompany';
import { paths } from '../../RouterContainer';
import { columns } from '../../../components/columns/Company/InvoiceOverviewColumns';
import { onDownloadItem } from '../../../utils/functions/file.functions';
import Button from '../../../components/button/Button';
import {
  DirectionDoc,
  DirectionDocTranslated,
  DocumentsFilter,
  DocumentType,
  DocumentTypeTranslated,
  InvoiceBase,
} from '../../../types/Invoice';
import { useTranslation } from '../../../context/translationContext';
import { TableFilter } from '../../../components/fIlter/TableFilter';
import { ObjectFromEnum, convertEnumToArray } from '../../../utils/functions/data.functions';
import { DocumentArrowUpIcon, PlusIcon } from '@heroicons/react/24/outline';
import format from 'date-fns/format';
import { dateSubmitFormat } from '../../../types/core/date.types';
import { startOfYear } from 'date-fns';
import { FilterItemType } from '../../../components/fIlter/TableFilter.types';
import { useBreadcrumbs } from '../../../hooks/useBreadcrumbs';
import { InvoiceCopy } from '../../../components/invoice/InvoiceCopy';
import { handleApiErrors } from '../../../api/apiErrorHandler';
import { useLoading } from '../../../context/loadingContext';
import useSessionStorage from '../../../hooks/useSessionStorage';
import { sessionStorageKeys } from '../../../utils/keys/sessionStorageKeys';
import { InvoiceUploadToCreateModal } from './InvoiceUploadToCreateModal';

/**
 * Page component represents list of Invoices for current company
 */
export const InvoicePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<InvoiceBase[] | null>(null);
  const [filteredInvoices, setFilteredInvoices] = useState<InvoiceBase[] | null>(null);
  const [isUploadToCreateOpen, setIsUploadToCreateOpen] = useState<boolean>(false);
  const { company } = useCurrentCompany();
  /**
   * Filter default values
   */
  const defaultFilterValues = {
    documentType: undefined,
    filterFrom: startOfYear(new Date()),
    filterTo: undefined,
    documentDirection: DirectionDoc.OUTPUT_DOC,
  };
  const [filter, setFilter] = useSessionStorage<DocumentsFilter>(
    sessionStorageKeys.OVERVIEW_FILTER + 'documents',
    defaultFilterValues,
  );
  const { setBreadcrumbs } = useBreadcrumbs();
  const [selectedDocumentId, setSelectedDocumentId] = useState<string>();
  const [openCopyModal, setOpenCopyModal] = useState<boolean>(false);
  const { startLoading, stopLoading } = useLoading();
  useEffect(() => {
    setBreadcrumbs([{ path: paths.INVOICES, name: 'navigation.documents' }]);
  }, []);

  /**
   * Loads list of invoices for current company by selected filter
   */
  const fetchInvoices = async () => {
    if (!filter) return;
    let req = `?companyId=${company?.id}`;
    if (filter.documentDirection) req += `&documentDirection=${filter.documentDirection}`;
    if (filter.documentType) req += `&documentType=${filter.documentType}`;
    if (filter.filterFrom)
      req += `&filterFrom=${format(new Date(filter.filterFrom), dateSubmitFormat)}`;
    if (filter.filterTo) req += `&filterTo=${format(new Date(filter.filterTo), dateSubmitFormat)}`;
    const response = await http.apiGet({
      url: `${apiUrl.ACCOUNTING_DOCUMENT_LIST}${req}`,
    });
    if (response?.data) {
      setInvoices(response?.data);
      setFilteredInvoices(response?.data);
    }
  };

  useEffect(() => {
    if (!company) return;
    setFilteredInvoices(null);
    fetchInvoices();
  }, [filter, company]);

  /**
   * Redirects on Edit invoice page
   * @param id
   * @returns
   */
  const onEditItem = (id: string | number) => {
    if (!id) return;
    navigate(`/${paths.INVOICE}/${id}`);
  };

  /**
   * Deletes invoice
   * @param id
   */
  const onRemoveItem = async (id: string | number) => {
    if (!id) return;
    const response = await http.apiDelete({
      url: `${apiUrl.ACCOUNTING_DOCUMENT_DELETE}?id=${id}`,
    });
    if (response?.status === 204) {
      toast.success('Success!');
      fetchInvoices();
    }
  };

  /**
   * Downloads selected invoice
   * @param id of invoice
   */
  const handleDownloadInvoice = async (id: string | number) => {
    if (!id || !invoices) return;
    const fileName = invoices.find((f: InvoiceBase) => f.id === id)?.documentNumber;

    try {
      startLoading();
      // generate document pdf
      await http
        .apiGet({
          url: `${apiUrl.ACCOUNTING_DOCUMENT_GET_PDF}?id=${id}`,
        })
        .then(async (res) => {
          if (!res) return;
          // download pdf
          onDownloadItem(`${apiUrl.FILE_DOWNLOAD}?id=${res.data}`, fileName);
          stopLoading();
        });
    } catch (err) {
      stopLoading();
      handleApiErrors(err);
    }
  };

  /**
   * Creates a copy of selected document
   * @param id of document
   */
  const handleCopyDocument = async (id: string) => {
    if (!id) return;
    setSelectedDocumentId(id);
  };

  useEffect(() => {
    if (!selectedDocumentId) return;
    setOpenCopyModal(true);
  }, [selectedDocumentId]);

  return (
    <>
      <div className="w-full space-y-8">
        <div className="flex justify-between">
          <h1 className="text-3xl font-bold">{t('page.invoice.document-overview-headline')}</h1>
          <div>
            <Button
              className="mr-3"
              onClick={() => setIsUploadToCreateOpen(true)}
              title={t('page.invoice.upload-to-create')}
              showLoading
              icon={<DocumentArrowUpIcon className="w-4 h-4 mr-1 cursor-pointer" />}
            />
            <Link to={`/${paths.NEW_INVOICE}`}>
              <Button
                title={t('general.create-new')}
                color="green"
                icon={<PlusIcon className="w-4 h-4 mr-3" />}
              />
            </Link>
          </div>
        </div>
        {/* Document direction filter */}
        <div className="text-center items-center justify-center transition duration-150 ease-in-out ">
          {convertEnumToArray(DirectionDoc, DirectionDocTranslated, t).map(
            (direction: ObjectFromEnum, index: number) => (
              <Button
                key={index}
                title={direction.name}
                className={`inline-block bg-white shadow-md border-black px-24 pb-2 pt-2.5 ${
                  index === 0
                    ? `rounded-l-full`
                    : index === Object.keys(DirectionDoc).length - 1
                    ? `rounded-r-full`
                    : `rounded-none`
                } text-md font-large uppercase leading-normal transition duration-150 ease-in-out hover:text-white ${
                  filter?.documentDirection === direction.value
                    ? `bg-blue-600 text-white`
                    : `bg-[#fff] text-black`
                }`}
                onClick={() =>
                  setFilter({
                    ...filter,
                    documentDirection:
                      filter?.documentDirection === direction.value ? undefined : direction.value,
                  })
                }
              />
            ),
          )}
        </div>
        <TableFilter
          filterId="documents"
          filter={filter}
          defaultFilterValues={defaultFilterValues}
          setFilter={setFilter}
          filterOptions={[
            {
              data: [
                { value: undefined, name: t('general.select') },
                ...convertEnumToArray(DocumentType, DocumentTypeTranslated, t),
              ],
              title: t('document-type.headline'),
              type: FilterItemType.SELECT,
              valueKey: 'documentType',
              valueId: 'value',
            },
            {
              type: FilterItemType.DATE,
              title: t('general.date-from'),
              valueKey: 'filterFrom',
            },
            {
              type: FilterItemType.DATE,
              title: t('general.date-to'),
              valueKey: 'filterTo',
            },
            {
              data: invoices,
              type: FilterItemType.FULLTEXT,
              setData: setFilteredInvoices,
              includedFields: [
                'documentNumber',
                'currency',
                'bankAccountNumber',
                'language',
                'supplierAddressCity',
                'supplierAddressLine1',
                'supplierAddressLine2',
                'supplierAddressPostCode',
                'supplierCompanyId',
                'supplierName',
                'supplierVatId',
                'subscriberAddressCity',
                'subscriberAddressLine1',
                'subscriberAddressLine2',
                'subscriberAddressPostCode',
                'subscriberAddressCountry',
                'subscriberCompanyId',
                'subscriberName',
                'subscriberVatId',
                // items obj
                'items',
                'codeBookItemCode',
                'name',
                'vatCode',
              ],
            },
          ]}
        />
        <DataTable
          data={filteredInvoices}
          columns={columns()}
          handleEdit={onEditItem}
          handleRemove={onRemoveItem}
          columnFilters
          customButtons={[
            {
              title: 'Download',
              color: 'blue',
              showLoading: true,
              onSubmit: (e) => handleDownloadInvoice(e as string),
            },
            {
              title: t('general.copy'),
              color: 'blue',
              onSubmit: (e) => handleCopyDocument(e as string),
            },
          ]}
        />
        {/* Dialog wherther user wants to copy linked documents  */}
        {selectedDocumentId && (
          <InvoiceCopy
            documentId={selectedDocumentId}
            onClose={() => setOpenCopyModal(false)}
            open={openCopyModal}
          />
        )}
      </div>
      {/* Dialog for uploading file to create documents */}
      <InvoiceUploadToCreateModal
        open={isUploadToCreateOpen}
        setOpen={setIsUploadToCreateOpen}
        onClose={fetchInvoices}
      />
    </>
  );
};
